import { asArray } from "@/shared/utils/helpers";
import { useEffect, useMemo } from "react";
import useBranch from "../atoms/useBranch";
import useLayoutQuery from "../queries/branch/useLayoutQuery";
import { countryLanguages } from "../utils/constant";
import { Franchise, MerchantResponse } from "../utils/interfaces";
import useFranchiseHook from "./useFranchiseHook";
import useMerchantHook from "./useMerchantHook";
const useLayoutData = () => {
  const [branch, setBranch] = useBranch();

  const query = useLayoutQuery();
  const branches = useMemo(() => query?.data?.branches, [query]);
  const franchises = useMemo<Franchise[]>(() => {
    const set = new Set<string>();
    branches?.forEach(
      (branch) =>
        branch.franchise !== undefined &&
        set.add(JSON.stringify(branch?.franchise))
    );
    const res = Array.from(set).map((v) => JSON.parse(v));
    return res;
  }, [branches]);

  const merchants = useMemo<MerchantResponse[]>(() => {
    const set = new Set<string>();
    branches?.forEach(
      (branch) =>
        branch.merchant !== undefined &&
        set.add(JSON.stringify(branch?.merchant))
    );
    const res = Array.from(set).map((v) => JSON.parse(v));
    return res;
  }, [branches]);

  const [franchise, setFranchise] = useFranchiseHook();
  const [merchant, setMerchant] = useMerchantHook();

  const currentBranches = useMemo(
    () => branches?.filter((branch) => branch?.franchise) || [],
    [branches]
  );

  useEffect(() => {
    branches?.length === 0 && setBranch(branches[0].branch);
  }, [branches]);

  const findBranch = (id: number) =>
    query?.data?.branches.find((group) => group.branch.id === id);

  const findFranchise = (id: number) =>
    franchises.find((franchise) => franchise.id === id);

  const findMerchant = (id: number) =>
    merchants.find((merchant) => merchant.id === id);

  const country = findFranchise(franchise)?.country;
  const currentFranchise = findFranchise(franchise);

  const franchiseLanguages = useMemo(
    () => [
      countryLanguages[0],
      ...countryLanguages.filter((l) =>
        asArray(currentFranchise?.languages).includes(l.value)
      ),
    ],
    [countryLanguages, currentFranchise?.languages]
  );

  const supportedLanguagesKey = useMemo(
    () =>
      franchiseLanguages.reduce(
        (acc, cur) => (acc = { ...acc, [cur.key]: "" }),
        {}
      ),
    [franchiseLanguages]
  );

  const startPage = useMemo(() => query?.data?.start_page, [query.data]);

  useEffect(() => {
    if (country > 0) {
      localStorage.setItem("country", `${country}`);
    }
  }, [country]);

  return {
    franchises,
    franchise,
    setFranchise,
    currentBranches,
    ...query,
    branch,
    country,
    branches,
    setBranch,
    findBranch,
    findFranchise,
    findMerchant,
    merchant,
    setMerchant,
    franchiseLanguages,
    supportedLanguagesKey,
    startPage,
  };
};

export default useLayoutData;
